import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { FieldError, FormGroup, FormRow, Input, Label, Select } from './TicketStyledComponents';

interface Counterparty {
  id: number;
  counterpartyName: string;
  counterpartyCode?: string;
  counterpartyType?: string;
  isActive?: boolean;
}

export interface ExecutionClearingConfig {
  enabled: boolean;
  showCounterparty?: boolean;
  showExecutionBroker?: boolean;
  showExecutionVenue?: boolean;
  showExecutionMethod?: boolean;
  showClearing?: boolean;
  showPrimeBroker?: boolean;
  showIsda?: boolean;
  showSettlementInstruction?: boolean;
  counterpartyLabel?: string;
  executionBrokerLabel?: string;
  /**
   * F-FX-023: venue list for this asset class. The global list is credit- and
   * exchange-oriented; an asset class whose venues differ supplies its own.
   */
  executionVenueOptions?: string[];
  /**
   * F-REPO-018: clearing-house list for this asset class. The global list is
   * derivatives-oriented (CME/ICE/OCC are not repo CCPs); repo restricts to
   * the CCPs that actually clear repo (FICC GSD, LCH RepoClear).
   */
  clearingHouseOptions?: string[];
}

interface ExecutionClearingSectionProps {
  formData: any;
  errors: { [key: string]: string };
  isFieldDisabled: (field: string) => boolean;
  handleInputChange: (field: string, value: string) => void;
  config: ExecutionClearingConfig;
}

export const executionClearingInitialFields = {
  counterparty: '',
  executionBroker: '',
  ecnPlatform: '',
  executionVenue: '',
  executionMethod: '',
  clearingBrokerFcm: '',
  clearingHouse: '',
  primeBroker: '',
  clearingAccount: '',
  isCleared: '',
  isdaAgreement: '',
  csaAgreement: '',
  settlementInstruction: '',
};

const EXECUTION_METHOD_OPTIONS = [
  ['RFQ', 'RFQ'],
  ['VOICE', 'Voice'],
  ['FIX', 'FIX'],
  ['EMSX', 'Bloomberg EMSX'],
  ['EXCHANGE', 'Exchange'],
  ['OTC_BILATERAL', 'OTC Bilateral'],
  ['CLEARINGHOUSE', 'Clearing House'],
];

const EXECUTION_VENUE_OPTIONS = [
  'Tradeweb',
  'MarketAxess',
  'Bloomberg EMSX',
  'Bloomberg FXGO',
  'CME Globex',
  'ICE',
  'Cboe',
  'OTC Voice',
];

/**
 * F-FX-023: FX does not trade on the credit/exchange venues above. MarketAxess
 * has no FX product at all, and an FX spot booked to ICE or Cboe is not a
 * mislabel that stays cosmetic — execution venue is what TCA, best-execution
 * reporting and venue analytics are computed from. These are the venues an FX
 * desk actually deals on; deliverable forwards and NDFs are dealt on the same
 * platforms or by voice.
 */
const FX_EXECUTION_VENUE_OPTIONS = [
  'EBS',
  'LSEG Matching',
  'FXall',
  '360T',
  'Hotspot',
  'Currenex',
  'Bloomberg FXGO',
  'Tradeweb FX',
  'OTC Voice',
];

const CLEARING_HOUSE_OPTIONS = [
  'CME Clearing',
  'ICE Clear',
  'LCH',
  'OCC',
  'DTCC NSCC',
  'FICC',
];

// Collapsed by default (operator request 2026-08-05 — parity with the
// Allocation Grid / Notes / Trade Rationale expanders); auto-expands when
// validation errors land on its fields so an execute refusal is never hidden
// inside a collapsed panel.
const ExpanderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  cursor: pointer;
  user-select: none;
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border-top: 1px solid rgba(148, 163, 184, 0.15);
  &:hover { color: #fff; }
`;

const ExpanderBadge = styled.span`
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
  font-size: 10px;
  font-weight: 800;
`;

export const EC_FIELD_KEYS = [
  'counterparty', 'executionBroker', 'executionVenue', 'executionMethod',
  'primeBroker', 'isCleared', 'clearingBrokerFcm', 'clearingHouse',
  'clearingAccount', 'isdaAgreement', 'csaAgreement', 'settlementInstruction',
];

const ExecutionBand = styled.div`
  margin-bottom: 18px;
  padding: 16px;
  border-radius: 10px;
  border: 1px solid rgba(16, 185, 129, 0.26);
  background: linear-gradient(180deg, rgba(6, 78, 59, 0.18) 0%, rgba(15, 23, 42, 0.48) 100%);
`;

const ExecutionBandHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

const ExecutionBandTitle = styled.div`
  color: #a7f3d0;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const ExecutionBadge = styled.div`
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.14);
  border: 1px solid rgba(16, 185, 129, 0.28);
  color: #34d399;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const ExecutionSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px;
`;

const ExecutionSummaryItem = styled.div`
  min-width: 0;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid rgba(16, 185, 129, 0.18);
  background: rgba(15, 23, 42, 0.62);
`;

const ExecutionSummaryLabel = styled.div`
  color: #6ee7b7;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const ExecutionSummaryValue = styled.div`
  color: #d1fae5;
  font-size: 14px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const normalize = (value: any): string | null => {
  if (value == null) return null;
  const str = String(value).trim();
  return str.length > 0 ? str : null;
};

const normalizeBoolean = (value: any): boolean | null => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return null;
};

export const buildExecutionClearingPayload = (formData: any) => ({
  counterparty: normalize(formData.counterparty || formData.counterpartyName),
  executionBroker: normalize(formData.executionBroker),
  ecnPlatform: normalize(formData.ecnPlatform),
  executionVenue: normalize(formData.executionVenue),
  executionMethod: normalize(formData.executionMethod),
  clearingBrokerFcm: normalize(formData.clearingBrokerFcm),
  clearingHouse: normalize(formData.clearingHouse),
  primeBroker: normalize(formData.primeBroker),
  clearingAccount: normalize(formData.clearingAccount),
  isCleared: normalizeBoolean(formData.isCleared),
  isdaAgreement: normalize(formData.isdaAgreement),
  csaAgreement: normalize(formData.csaAgreement),
  settlementInstruction: normalize(formData.settlementInstruction),
});

export const loadExecutionClearingFields = (transaction: any) => ({
  ...executionClearingInitialFields,
  counterparty: transaction?.counterparty || '',
  executionBroker: transaction?.executionBroker || '',
  ecnPlatform: transaction?.ecnPlatform || '',
  executionVenue: transaction?.executionVenue || '',
  executionMethod: transaction?.executionMethod || '',
  clearingBrokerFcm: transaction?.clearingBrokerFcm || '',
  clearingHouse: transaction?.clearingHouse || '',
  primeBroker: transaction?.primeBroker || '',
  clearingAccount: transaction?.clearingAccount || '',
  isCleared: transaction?.isCleared == null ? '' : String(transaction.isCleared),
  isdaAgreement: transaction?.isdaAgreement || '',
  csaAgreement: transaction?.csaAgreement || '',
  settlementInstruction: transaction?.settlementInstruction || '',
});

export const getTicketExecutionClearingConfig = (assetClass?: string, assetSubClass?: string): ExecutionClearingConfig => {
  const ac = (assetClass || '').toUpperCase();
  const sub = (assetSubClass || '').toUpperCase();
  if (
    ac.includes('PRIVATE EQUITY') ||
    ac.includes('PRIVATE FIXED') ||
    sub.includes('PRIVATE CREDIT') ||
    sub.includes('PE COMMITMENT') ||
    ac.includes('REAL ESTATE')
  ) {
    return { enabled: false };
  }

  const publicCredit = ac.includes('FIXED') || ac.includes('MBS') || ac.includes('CLO') || ac.includes('ABF');
  const exchangeDerivative = sub.includes('FUTURES') || sub.includes('OPTIONS');
  const otcDerivative = ac.includes('DERIVATIVE') && !exchangeDerivative;

  if (publicCredit) {
    return {
      enabled: true,
      showExecutionBroker: true,
      showExecutionVenue: true,
      showExecutionMethod: true,
      showSettlementInstruction: true,
      executionBrokerLabel: 'Broker / Dealer',
    };
  }

  if (ac.includes('EQUITY')) {
    return {
      enabled: true,
      showExecutionBroker: true,
      showExecutionVenue: true,
      showExecutionMethod: true,
      showPrimeBroker: true,
      showSettlementInstruction: true,
    };
  }

  if (ac.includes('FX')) {
    return {
      enabled: true,
      // F-FX-024: FX was the only lane capturing counterparty as free text —
      // and the one that writes it into its silo. "GS" and
      // "Goldman Sachs & Co. LLC" aggregate as two names, which breaks the
      // counterparty exposure a deliverable forward actually carries.
      showCounterparty: true,
      counterpartyLabel: 'FX Counterparty',
      showExecutionBroker: true,
      showExecutionVenue: true,
      showExecutionMethod: true,
      showPrimeBroker: true,
      showSettlementInstruction: true,
      executionVenueOptions: FX_EXECUTION_VENUE_OPTIONS,
    };
  }

  if (exchangeDerivative) {
    return {
      enabled: true,
      showExecutionBroker: true,
      showExecutionVenue: true,
      showExecutionMethod: true,
      showClearing: true,
      showPrimeBroker: true,
      showSettlementInstruction: true,
    };
  }

  if (otcDerivative) {
    return {
      enabled: true,
      showCounterparty: true,
      showExecutionBroker: true,
      showExecutionMethod: true,
      showClearing: true,
      showIsda: true,
      showSettlementInstruction: true,
      counterpartyLabel: 'Swap Counterparty',
    };
  }

  if (ac.includes('FINANCING') || sub.includes('REPO')) {
    return {
      enabled: true,
      showExecutionBroker: true,
      showExecutionMethod: true,
      showClearing: true,
      showSettlementInstruction: true,
      executionBrokerLabel: 'Broker / Financing Counterparty',
      // F-REPO-018: only CCPs that actually clear repo.
      clearingHouseOptions: ['FICC', 'LCH'],
    };
  }

  return { enabled: false };
};

export const validateExecutionClearingFields = (
  formData: any,
  config: ExecutionClearingConfig,
  action?: string,
): { [key: string]: string } => {
  const errors: { [key: string]: string } = {};
  if (!config.enabled) return errors;

  const isCleared = normalizeBoolean(formData.isCleared);

  // Operator finding 2026-08-05: tickets that carry broker / settlement
  // information must have it BEFORE execution — a trade executed with no
  // broker, no cleared answer and no settlement instruction has no
  // operational trail. Not required at draft/pre-clear so idea-stage
  // tickets stay friction-free. That includes the cleared-conditional
  // FCM / clearing-house pair (F-CDS-012, 2026-08-06): it used to fire on
  // every action, which went unnoticed while tickets left isCleared blank
  // until execution — the CDS ticket's contingent leg defaults cleared=Yes
  // and was blocked at DRAFT save.
  if (action === 'execute') {
    if (config.showClearing && isCleared === true) {
      if (!normalize(formData.clearingBrokerFcm)) {
        errors.clearingBrokerFcm = 'Clearing broker / FCM is required when cleared';
      }
      if (!normalize(formData.clearingHouse)) {
        errors.clearingHouse = 'Clearing house is required when cleared';
      }
    }
    const externallyExecuted = !!(formData.ecnPlatform || formData.executionId || formData.executionTime);
    if (!externallyExecuted) {
      if (config.showExecutionBroker && !normalize(formData.executionBroker)) {
        errors.executionBroker = (config.executionBrokerLabel || 'Execution broker') + ' is required before execution';
      }
      if (config.showCounterparty && !normalize(formData.counterparty)) {
        errors.counterparty = (config.counterpartyLabel || 'Counterparty') + ' is required before execution';
      }
    }
    if (config.showClearing && isCleared === null) {
      errors.isCleared = 'Specify whether the trade is cleared before execution';
    }
    if (config.showSettlementInstruction && !normalize(formData.settlementInstruction)) {
      errors.settlementInstruction = 'Settlement instruction is required before execution';
    }
  }

  return errors;
};

const counterpartyOptionLabel = (cp: Counterparty) => {
  const suffix = [cp.counterpartyCode, cp.counterpartyType].filter(Boolean).join(' / ');
  return suffix ? `${cp.counterpartyName} (${suffix})` : cp.counterpartyName;
};

const hasCurrentCounterpartyOption = (list: Counterparty[], value: any) => {
  const current = normalize(value);
  return !current || list.some((cp) => cp.counterpartyName === current);
};

const hasCurrentStringOption = (list: string[], value: any) => {
  const current = normalize(value);
  return !current || list.includes(current);
};

const formatExecutionTime = (value: any) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const ExecutionClearingSection: React.FC<ExecutionClearingSectionProps> = ({
  formData,
  errors,
  isFieldDisabled,
  handleInputChange,
  config,
}) => {
  const [counterparties, setCounterparties] = useState<Counterparty[]>([]);
  // F-FX-023: asset classes whose venues differ from the credit/exchange
  // default supply their own list.
  const venueOptions = config.executionVenueOptions ?? EXECUTION_VENUE_OPTIONS;
  const clearingHouseOptions = config.clearingHouseOptions ?? CLEARING_HOUSE_OPTIONS;

  useEffect(() => {
    let cancelled = false;
    if (!config.enabled) {
      setCounterparties([]);
      return;
    }

    (async () => {
      try {
        const { data } = await axios.get('/api/credit/counterparties');
        if (!cancelled) {
          setCounterparties(Array.isArray(data) ? data.filter((cp) => cp?.isActive !== false) : []);
        }
      } catch (error) {
        if (!cancelled) setCounterparties([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [config.enabled]);

  const dealerCounterparties = useMemo(
    () => counterparties.filter((cp) => ['DEALER', 'BANK', 'CLEARINGHOUSE'].includes(cp.counterpartyType || '')),
    [counterparties],
  );

  const clearingCounterparties = useMemo(
    () => counterparties.filter((cp) => ['CLEARINGHOUSE', 'BANK', 'DEALER'].includes(cp.counterpartyType || '')),
    [counterparties],
  );

  const hasExternalExecution = !!(formData.ecnPlatform || formData.executionId || formData.executionTime);
  const hasSectionErrors = EC_FIELD_KEYS.some((k) => !!errors[k]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (hasSectionErrors || hasExternalExecution) setOpen(true);
  }, [hasSectionErrors, hasExternalExecution]);

  if (!config.enabled) return null;

  const disabled = (field: string) => isFieldDisabled(field);
  const showEditableExecutionFields = !hasExternalExecution;
  const hasPrimaryFields = !!(
    config.showCounterparty ||
    (config.showExecutionBroker && showEditableExecutionFields) ||
    (config.showExecutionVenue && showEditableExecutionFields) ||
    (config.showExecutionMethod && showEditableExecutionFields) ||
    config.showPrimeBroker
  );

  return (
    <>
      <ExpanderHeader onClick={() => setOpen((v) => !v)}>
        {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        <span>Execution & Clearing</span>
        {hasSectionErrors && <ExpanderBadge>required</ExpanderBadge>}
      </ExpanderHeader>
      {open && (<>
      {hasExternalExecution && (
        <ExecutionBand>
          <ExecutionBandHeader>
            <ExecutionBandTitle>Execution Confirmed</ExecutionBandTitle>
            <ExecutionBadge>{formData.ecnPlatform || formData.executionVenue || 'Executed'}</ExecutionBadge>
          </ExecutionBandHeader>
          <ExecutionSummaryGrid>
            <ExecutionSummaryItem>
              <ExecutionSummaryLabel>ECN Platform</ExecutionSummaryLabel>
              <ExecutionSummaryValue>{formData.ecnPlatform || '-'}</ExecutionSummaryValue>
            </ExecutionSummaryItem>
            <ExecutionSummaryItem>
              <ExecutionSummaryLabel>Execution Broker</ExecutionSummaryLabel>
              <ExecutionSummaryValue>{formData.executionBroker || '-'}</ExecutionSummaryValue>
            </ExecutionSummaryItem>
            <ExecutionSummaryItem>
              <ExecutionSummaryLabel>Execution ID</ExecutionSummaryLabel>
              <ExecutionSummaryValue>{formData.executionId || '-'}</ExecutionSummaryValue>
            </ExecutionSummaryItem>
            <ExecutionSummaryItem>
              <ExecutionSummaryLabel>Execution Time</ExecutionSummaryLabel>
              <ExecutionSummaryValue>{formatExecutionTime(formData.executionTime)}</ExecutionSummaryValue>
            </ExecutionSummaryItem>
          </ExecutionSummaryGrid>
        </ExecutionBand>
      )}
      {hasPrimaryFields && (
        <FormRow>
          {config.showCounterparty && (
            <FormGroup>
              <Label htmlFor="counterparty">{config.counterpartyLabel || 'Counterparty'}</Label>
              <Select
                id="counterparty"
                value={formData.counterparty || ''}
                onChange={(e) => handleInputChange('counterparty', e.target.value)}
                disabled={disabled('counterparty')}
                error={!!errors.counterparty}
              >
                <option value="">Select Counterparty</option>
                {!hasCurrentCounterpartyOption(counterparties, formData.counterparty) && (
                  <option value={formData.counterparty}>{formData.counterparty}</option>
                )}
                {counterparties.map((cp) => (
                  <option key={cp.id} value={cp.counterpartyName}>
                    {counterpartyOptionLabel(cp)}
                  </option>
                ))}
              </Select>
              {errors.counterparty && <FieldError>{errors.counterparty}</FieldError>}
            </FormGroup>
          )}

          {config.showExecutionBroker && showEditableExecutionFields && (
            <FormGroup>
              <Label htmlFor="executionBroker">{config.executionBrokerLabel || 'Execution Broker'}</Label>
              <Select
                id="executionBroker"
                value={formData.executionBroker || ''}
                onChange={(e) => handleInputChange('executionBroker', e.target.value)}
                disabled={disabled('executionBroker')}
                error={!!errors.executionBroker}
              >
                <option value="">Select Broker</option>
                {!hasCurrentCounterpartyOption(dealerCounterparties, formData.executionBroker) && (
                  <option value={formData.executionBroker}>{formData.executionBroker}</option>
                )}
                {dealerCounterparties.map((cp) => (
                  <option key={cp.id} value={cp.counterpartyName}>
                    {counterpartyOptionLabel(cp)}
                  </option>
                ))}
              </Select>
              {errors.executionBroker && <FieldError>{errors.executionBroker}</FieldError>}
            </FormGroup>
          )}

          {config.showExecutionVenue && showEditableExecutionFields && (
            <FormGroup>
              <Label htmlFor="executionVenue">Execution Venue</Label>
              <Select
                id="executionVenue"
                value={formData.executionVenue || ''}
                onChange={(e) => handleInputChange('executionVenue', e.target.value)}
                disabled={disabled('executionVenue')}
                error={!!errors.executionVenue}
              >
                <option value="">Select Venue</option>
                {!hasCurrentStringOption(venueOptions, formData.executionVenue) && (
                  <option value={formData.executionVenue}>{formData.executionVenue}</option>
                )}
                {venueOptions.map((venue) => (
                  <option key={venue} value={venue}>{venue}</option>
                ))}
              </Select>
              {errors.executionVenue && <FieldError>{errors.executionVenue}</FieldError>}
            </FormGroup>
          )}

          {config.showExecutionMethod && showEditableExecutionFields && (
            <FormGroup>
              <Label htmlFor="executionMethod">Execution Method</Label>
              <Select
                id="executionMethod"
                value={formData.executionMethod || ''}
                onChange={(e) => handleInputChange('executionMethod', e.target.value)}
                disabled={disabled('executionMethod')}
                error={!!errors.executionMethod}
              >
                <option value="">Select Method</option>
                {!EXECUTION_METHOD_OPTIONS.some(([value]) => value === formData.executionMethod) && formData.executionMethod && (
                  <option value={formData.executionMethod}>{formData.executionMethod}</option>
                )}
                {EXECUTION_METHOD_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
              {errors.executionMethod && <FieldError>{errors.executionMethod}</FieldError>}
            </FormGroup>
          )}

          {config.showPrimeBroker && (
            <FormGroup>
              <Label htmlFor="primeBroker">Prime Broker</Label>
              <Select
                id="primeBroker"
                value={formData.primeBroker || ''}
                onChange={(e) => handleInputChange('primeBroker', e.target.value)}
                disabled={disabled('primeBroker')}
                error={!!errors.primeBroker}
              >
                <option value="">Select Prime Broker</option>
                {!hasCurrentCounterpartyOption(dealerCounterparties, formData.primeBroker) && (
                  <option value={formData.primeBroker}>{formData.primeBroker}</option>
                )}
                {dealerCounterparties.map((cp) => (
                  <option key={cp.id} value={cp.counterpartyName}>
                    {counterpartyOptionLabel(cp)}
                  </option>
                ))}
              </Select>
              {errors.primeBroker && <FieldError>{errors.primeBroker}</FieldError>}
            </FormGroup>
          )}
        </FormRow>
      )}

      {config.showClearing && (
        <FormRow>
          <FormGroup>
            <Label htmlFor="isCleared">Cleared</Label>
            <Select
              id="isCleared"
              value={formData.isCleared || ''}
              onChange={(e) => handleInputChange('isCleared', e.target.value)}
              disabled={disabled('isCleared')}
              error={!!errors.isCleared}
            >
              <option value="">Select</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
            {errors.isCleared && <FieldError>{errors.isCleared}</FieldError>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="clearingBrokerFcm">Clearing Broker / FCM</Label>
            <Select
              id="clearingBrokerFcm"
              value={formData.clearingBrokerFcm || ''}
              onChange={(e) => handleInputChange('clearingBrokerFcm', e.target.value)}
              disabled={disabled('clearingBrokerFcm')}
              error={!!errors.clearingBrokerFcm}
            >
              <option value="">Select Clearing Broker</option>
              {!hasCurrentCounterpartyOption(clearingCounterparties, formData.clearingBrokerFcm) && (
                <option value={formData.clearingBrokerFcm}>{formData.clearingBrokerFcm}</option>
              )}
              {clearingCounterparties.map((cp) => (
                <option key={cp.id} value={cp.counterpartyName}>
                  {counterpartyOptionLabel(cp)}
                </option>
              ))}
            </Select>
            {errors.clearingBrokerFcm && <FieldError>{errors.clearingBrokerFcm}</FieldError>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="clearingHouse">Clearing House</Label>
            <Select
              id="clearingHouse"
              value={formData.clearingHouse || ''}
              onChange={(e) => handleInputChange('clearingHouse', e.target.value)}
              disabled={disabled('clearingHouse')}
              error={!!errors.clearingHouse}
            >
              <option value="">Select Clearing House</option>
              {!hasCurrentStringOption(clearingHouseOptions, formData.clearingHouse)
                && !hasCurrentCounterpartyOption(clearingCounterparties, formData.clearingHouse) && (
                <option value={formData.clearingHouse}>{formData.clearingHouse}</option>
              )}
              {clearingHouseOptions.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
              {clearingCounterparties.map((cp) => (
                <option key={`cp-${cp.id}`} value={cp.counterpartyName}>
                  {counterpartyOptionLabel(cp)}
                </option>
              ))}
            </Select>
            {errors.clearingHouse && <FieldError>{errors.clearingHouse}</FieldError>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="clearingAccount">Clearing Account</Label>
            <Input
              id="clearingAccount"
              value={formData.clearingAccount || ''}
              onChange={(e) => handleInputChange('clearingAccount', e.target.value)}
              disabled={disabled('clearingAccount')}
              error={!!errors.clearingAccount}
            />
            {errors.clearingAccount && <FieldError>{errors.clearingAccount}</FieldError>}
          </FormGroup>
        </FormRow>
      )}

      {(config.showIsda || config.showSettlementInstruction) && (
        <FormRow>
          {config.showIsda && (
            <>
              <FormGroup>
                <Label htmlFor="isdaAgreement">ISDA Agreement</Label>
                <Input
                  id="isdaAgreement"
                  value={formData.isdaAgreement || ''}
                  onChange={(e) => handleInputChange('isdaAgreement', e.target.value)}
                  disabled={disabled('isdaAgreement')}
                  error={!!errors.isdaAgreement}
                />
                {errors.isdaAgreement && <FieldError>{errors.isdaAgreement}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="csaAgreement">CSA Agreement</Label>
                <Input
                  id="csaAgreement"
                  value={formData.csaAgreement || ''}
                  onChange={(e) => handleInputChange('csaAgreement', e.target.value)}
                  disabled={disabled('csaAgreement')}
                  error={!!errors.csaAgreement}
                />
                {errors.csaAgreement && <FieldError>{errors.csaAgreement}</FieldError>}
              </FormGroup>
            </>
          )}

          {config.showSettlementInstruction && (
            <FormGroup>
              <Label htmlFor="settlementInstruction">Settlement Instruction</Label>
              <Input
                id="settlementInstruction"
                value={formData.settlementInstruction || ''}
                onChange={(e) => handleInputChange('settlementInstruction', e.target.value)}
                disabled={disabled('settlementInstruction')}
                error={!!errors.settlementInstruction}
              />
              {errors.settlementInstruction && <FieldError>{errors.settlementInstruction}</FieldError>}
            </FormGroup>
          )}
        </FormRow>
      )}
      </>)}
    </>
  );
};

export default ExecutionClearingSection;
